import React from 'react';
import {
    Box,
    Button,
    Paper,
    Snackbar,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Dispatch, SetStateAction, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {getNearbyPubs, getPubRaycasting} from "../API";
import MapView from "./MapView";
import { themeColors } from '../styles/theme';
import EditLocationIcon from '@mui/icons-material/EditLocation';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import AdSense from './AdSense';
import Loader from "./Loader";
import ScrollIndicator from './ScrollIndicator';
import {
    trackAreaSearch,
    trackSearchResultsMetrics,
    trackFiltersApplied,
    trackSearchToViewConversion,
    trackViewPreference
} from "../utils/analytics";
import { getCountryFromLocale } from "../utils/getCountryFromLocale";
import { FiltersAndTabSelector } from "./FiltersAndTabSelector";
import TopNav from "./TopNav";
import { Footer } from './Footer';
import PubCard from './PubCard';
import { useLocation } from '../contexts/LocationContext';

export interface HeatmapData {
    timestamps: string[];
    values: number[];
}

export interface PubObject {
    name: string;
    distance: number;
    address?: string;
    city?: string;
    rating?: number;
    google_place_id: string;
    garden_drawn?: boolean;
    garden_moderated?: boolean;
    has_no_garden?: boolean;
    gardens?: Array<{
        name: string;
        uid: string;
        heatmap?: HeatmapData;
        is_uncertain?: boolean;
    }>;
    heatmap?: HeatmapData;
    latitude?: number;
    longitude?: number;
    sunrise?: string;
    sunset?: string;
    is_3d_supported?: boolean;
}

interface FindPubsProps {
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    setShowBurgerMenuIcon: Dispatch<SetStateAction<boolean>>;
    loading: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
}

interface GardenDataForAPI {
    heatmap_1d: HeatmapData;
    total_sun_points: number;
}

interface PubRaycastingResponse {
    [key: string]: GardenDataForAPI | string | unknown;
    pub_id: string;
    date: string;
    original_query_date: string;
    status?: string;
    has_no_garden?: boolean;
    garden_details?: Record<string, { name: string; is_uncertain: boolean }>;
    sunrise?: string;
    sunset?: string;
}

export const calculateBeerMugRating = (pointHours: number): number => {
    const ratingThresholds = { 1: 10, 2: 500, 3: 2000, 4: 10000, 5: 20000 };
    let rating = 1;
    for (let r = 5; r > 0; r--) {
        if (pointHours >= ratingThresholds[r as keyof typeof ratingThresholds]) {
            rating = r;
            break;
        }
    }
    return rating;
};

const FindPubs = ({
    search,
    setShowBurgerMenuIcon,
    loading: propLoading,
    setLoading: propSetLoading
}: FindPubsProps) => {
    const { 
        searchLocation,
        isCustomSearchMode,
        requestUserLocation,
        isLoadingUserLocation 
    } = useLocation();
    
    const [pubs, setPubs] = useState<PubObject[]>([]);
    const [tab, setTab] = useState<'list' | 'map'>('list');
    const navigate = useNavigate();
    const [raycastLoading, setRaycastLoading] = useState<boolean>(false);
    const [raycastingPubIds, setRaycastingPubIds] = useState<Set<string>>(new Set());

    const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [geoError, setGeoError] = useState<string | null>(null);
    const [is3DSupported, setIs3DSupported] = useState<boolean | null>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const timelineMode = isMobile ? 'compact' : 'full';

    // Use searchLocation from context, with fallback coordinates
    const latitude = searchLocation?.latitude ?? 51.4700777;
    const longitude = searchLocation?.longitude ?? -0.0245202;
    const isLoading = propLoading || isLoadingUserLocation;

    const hasInitiallyFetched = useRef<boolean>(false);
    const fetchCount = useRef(0);

    const [sortBy, setSortBy] = useState<'distance' | 'rating'>('distance');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [hideNoGardens, setHideNoGardens] = useState<boolean>(false);
    const [searchRadius, setSearchRadius] = useState<number>(() => {
        const savedParams = localStorage.getItem('searchAreaParams');
        if (savedParams) { try { return JSON.parse(savedParams).radius || 1000; } catch { /* ignore */ } }
        return 1000;
    });
    const [customRadiusInput, setCustomRadiusInput] = useState<number>(searchRadius);
    const [activeFilters, setActiveFilters] = useState<number>(0);
    const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState<null | HTMLElement>(null);

    useEffect(() => { window.scrollTo(0, 0); }, []);
    useEffect(() => { return () => { if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current); }; }, []);

    const handleEditSearchArea = () => { 
        navigate('/search-area'); 
    };

    useEffect(() => { setShowBurgerMenuIcon(false); }, [setShowBurgerMenuIcon]);

    const updatePubInArray = (pubsArray: PubObject[], pubId: string, updatedData: Partial<PubObject>): PubObject[] => {
        const index = pubsArray.findIndex(p => p.google_place_id === pubId);
        if (index === -1) return pubsArray;
        const newArray = [...pubsArray];
        newArray[index] = { ...newArray[index], ...updatedData };
        return newArray;
    };
    
    const fetchPubRaycasting = useCallback(async (pub: PubObject) => {
        // No need to setRaycastingPubIds here, it's done before calling this
        try {
            const response = await getPubRaycasting(pub.google_place_id, false, false);//, pub.latitude, pub.longitude);
            const pubApiData = response.data as PubRaycastingResponse;
            const dataToUpdate: Partial<PubObject> = {
                has_no_garden: pubApiData.has_no_garden || pubApiData.status === 'no_gardens' || pubApiData.status === 'no_garden',
                sunrise: pubApiData.sunrise,
                sunset: pubApiData.sunset,
            };

            if (!dataToUpdate.has_no_garden && pubApiData.garden_details) {
                const gardenDetails = pubApiData.garden_details;
                const gardenDetailKeys = Object.keys(gardenDetails);
                const heatmapKeys = Object.keys(pubApiData).filter(key =>
                    typeof pubApiData[key] === 'object' && pubApiData[key] !== null && (pubApiData[key] as any).heatmap_1d &&
                    !['pub_id', 'date', 'original_query_date', 'status', 'garden_details', 'has_no_garden', 'mesh_uris', 'centre_ecef', 'copyrights', 'sun_vector', 'sunrise', 'sunset', 'session_id'].includes(key)
                );
                const allGardenIds = Array.from(new Set([...gardenDetailKeys, ...heatmapKeys]));
                const processedGardens = allGardenIds
                    .filter(id => pubApiData[id] && (pubApiData[id] as any).heatmap_1d)
                    .map(subAreaId => {
                        const gardenApiData = pubApiData[subAreaId] as GardenDataForAPI;
                        return {
                            name: gardenDetails[subAreaId]?.name || `Garden ${subAreaId.substring(0,8)}`,
                            uid: subAreaId,
                            heatmap: gardenApiData.heatmap_1d,
                            is_uncertain: gardenDetails[subAreaId]?.is_uncertain || false,
                        };
                    });
                dataToUpdate.gardens = processedGardens;
                let totalSunPoints = 0;
                let gardensWithSunData = 0;
                processedGardens.forEach(g => {
                    const gardenApiData = pubApiData[g.uid] as GardenDataForAPI;
                    if (gardenApiData?.total_sun_points) { totalSunPoints += gardenApiData.total_sun_points; gardensWithSunData++; }
                });
                if (gardensWithSunData > 0) dataToUpdate.rating = calculateBeerMugRating(totalSunPoints / gardensWithSunData);
                if (processedGardens.length > 0 && processedGardens[0].heatmap) dataToUpdate.heatmap = processedGardens[0].heatmap;
                else if (pubApiData.heatmap_1d && typeof pubApiData.heatmap_1d !== 'string') dataToUpdate.heatmap = pubApiData.heatmap_1d as HeatmapData;
            }
            setPubs(prev => updatePubInArray(prev, pub.google_place_id, dataToUpdate));
        } catch (error) {
            console.error(`Error fetching raycasting data for pub ${pub.google_place_id}:`, error);
            // Optionally update the pub to indicate an error loading its details
            setPubs(prev => updatePubInArray(prev, pub.google_place_id, { heatmap: undefined /* or some error flag */ }));
        } finally {
            setRaycastingPubIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(pub.google_place_id);
                if (newSet.size === 0) {
                    setRaycastLoading(false); // All raycasting done
                }
                return newSet;
            });
        }
    }, []); // Removed setRaycastLoading from dependencies as it's handled internally

    useEffect(() => {
        if (search) return; // If search is active, list updates, no Lottie here.
        
        if (!searchLocation) {
            propSetLoading(false);
            return;
        }
        
        propSetLoading(true); // Show Lottie for initial pub list fetch
        hasInitiallyFetched.current = false; // Reset for this fetch cycle
        if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = setTimeout(() => {
            propSetLoading(false); 
            setRaycastLoading(false); // Also stop raycasting indication on timeout
            setGeoError("Loading timed out. Please refresh.");
        }, 30000);

        trackAreaSearch(latitude, longitude, searchRadius);

        const fetchPubsData = async () => {
            try {
                const country = getCountryFromLocale();
                const response = await getNearbyPubs(latitude, longitude, searchRadius, country);
                const fetchedPubs: PubObject[] = response.data.pubs || [];
                
                // Get 3D support status from response if available
                if (response.data.is_3d_supported !== undefined) {
                    setIs3DSupported(response.data.is_3d_supported);
                }
                
                setPubs(fetchedPubs); // Update pubs list
                hasInitiallyFetched.current = true; // Mark initial list as fetched
                trackSearchResultsMetrics(fetchedPubs.length, fetchedPubs.filter(p => p.garden_drawn).length, latitude, longitude, searchRadius);

                propSetLoading(false); // IMPORTANT: Turn off Lottie loader NOW. Pub cards will render.

                // Now, start fetching raycasting data in the background for supported pubs
                if (response.data.is_3d_supported !== false) {
                    const pubsWithGardens = fetchedPubs.filter(p => p.garden_drawn && !p.has_no_garden);
                    if (pubsWithGardens.length > 0) {
                        setRaycastingPubIds(new Set(pubsWithGardens.map(p => p.google_place_id)));
                        setRaycastLoading(true); // Indicate that some raycasting is happening

                        // Asynchronously fetch raycasting for all relevant pubs
                        Promise.allSettled(pubsWithGardens.map(p => fetchPubRaycasting(p)))
                            .then(() => {
                                // This .then() is called when all individual raycasting calls have settled.
                                // setRaycastLoading(false) is handled when raycastingPubIds becomes empty.
                            })
                            .catch(error => {
                                console.error("Error processing batch of pub raycasting calls:", error);
                            });
                    } else {
                        setRaycastLoading(false); // No gardens to raycast
                        setRaycastingPubIds(new Set());
                    }
                } else {
                    // If area is not 3D supported, don't even try to raycast
                    setRaycastLoading(false);
                    setRaycastingPubIds(new Set());
                }

            } catch (error: any) {
                console.error("Error fetching nearby pubs:", error); 
                setGeoError("Error loading pubs. Please refresh.");
                hasInitiallyFetched.current = true; // Still mark as fetched to show error message
                propSetLoading(false); // Ensure Lottie is off on error
            } finally {
                // propSetLoading(false) was moved up
                if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
            }
        };
        
        fetchPubsData();
        
        // Cleanup function for the timeout
        return () => {
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
        };
    }, [search, latitude, longitude, searchRadius, searchLocation, propSetLoading, fetchPubRaycasting, fetchCount.current]);

    const sortAndFilterPubs = useCallback((pubsToFilter: PubObject[], currentSearchTerm: string, currentHideNoGardens: boolean, currentSortBy: string, currentSortDirection: string) => {
        let filtered = [...pubsToFilter];
        if (currentSearchTerm) filtered = filtered.filter(pub => pub.name.toLowerCase().includes(currentSearchTerm.toLowerCase()));
        if (currentHideNoGardens) filtered = filtered.filter(pub => !pub.has_no_garden && (pub.garden_drawn || (pub.gardens && pub.gardens.length > 0)));
        return filtered.sort((a, b) => {
            let comparison = 0;
            if (currentSortBy === 'distance') comparison = a.distance - b.distance;
            else { const rA = a.rating || 0; const rB = b.rating || 0; comparison = rB === rA ? a.distance - b.distance : rB - rA; }
            return currentSortDirection === 'asc' ? comparison : -comparison;
        });
    }, []);

    const processedPubs = useMemo(() => {
        return sortAndFilterPubs(pubs, search, hideNoGardens, sortBy, sortDirection);
    }, [pubs, search, hideNoGardens, sortBy, sortDirection, sortAndFilterPubs]);

    useEffect(() => {
        let count = 0;
        if (hideNoGardens) count++; if (searchRadius !== 1000) count++;
        if (sortBy !== 'distance') count++; if (sortDirection !== 'asc') count++;
        setActiveFilters(count);
    }, [hideNoGardens, searchRadius, sortBy, sortDirection]);

    const handleFilterMenuClick = (event: React.MouseEvent<HTMLElement>) => setFilterMenuAnchorEl(event.currentTarget);
    const handleFilterMenuClose = () => setFilterMenuAnchorEl(null);

    const handleApplyFilters = () => {
        setSearchRadius(customRadiusInput);
        handleFilterMenuClose();
        trackFiltersApplied({ sortBy, sortDirection, hideNoGardens, customRadius: customRadiusInput });
        fetchCount.current += 1; 
        hasInitiallyFetched.current = false; // Force re-fetch with Lottie
    };

    const handleViewPub = useCallback((pubObjectToView: PubObject) => {
        trackSearchToViewConversion(pubObjectToView.google_place_id, (pubObjectToView.garden_drawn === true) || ((pubObjectToView.gardens?.length ?? 0) > 0), sortBy, { sortDirection, hideNoGardens, searchRadius });
        const completeData: PubObject = { ...pubObjectToView, name: pubObjectToView.name || 'Unknown Pub', google_place_id: pubObjectToView.google_place_id, latitude: pubObjectToView.latitude ?? 0, longitude: pubObjectToView.longitude ?? 0, address: pubObjectToView.address || '', garden_drawn: pubObjectToView.garden_drawn || false, garden_moderated: pubObjectToView.garden_moderated || false, has_no_garden: pubObjectToView.has_no_garden || false, distance: pubObjectToView.distance || 0, };
        navigate(`/pub/${pubObjectToView.google_place_id}`, { state: { pubToView: completeData } });
    }, [navigate, sortBy, sortDirection, hideNoGardens, searchRadius]);

    const handleSubmitGarden = useCallback((pubForSubmission: PubObject) => {
        const completeData: PubObject = { ...pubForSubmission, name: pubForSubmission.name || 'Unknown Pub', google_place_id: pubForSubmission.google_place_id, latitude: pubForSubmission.latitude ?? 0, longitude: pubForSubmission.longitude ?? 0, address: pubForSubmission.address || '', garden_drawn: pubForSubmission.garden_drawn || false, garden_moderated: pubForSubmission.garden_moderated || false, has_no_garden: pubForSubmission.has_no_garden || false, distance: pubForSubmission.distance || 0, };
        navigate(`/submit?pub_id=${pubForSubmission.google_place_id}`, { state: { selectedPub: completeData }});
    }, [navigate]);

    useEffect(() => { if (pubs.length > 0) trackViewPreference(tab, pubs.length); }, [tab, pubs.length]);

    return (
        <Box sx={{ width: '100%', minHeight: '100vh' }}>
            <TopNav title={'Find pubs'} />
            <FiltersAndTabSelector
                tab={tab} setTab={setTab}
                handleFilterMenuClick={handleFilterMenuClick} filterMenuAnchorEl={filterMenuAnchorEl}
                activeFilters={activeFilters} handleFilterMenuClose={handleFilterMenuClose}
                sortBy={sortBy} setSortBy={setSortBy}
                sortDirection={sortDirection} setSortDirection={setSortDirection}
                customRadius={customRadiusInput} setCustomRadius={setCustomRadiusInput}
                handleApplyFilters={handleApplyFilters}
                hideNoGardens={hideNoGardens} setHideNoGardens={setHideNoGardens}
                loading={isLoading && !hasInitiallyFetched.current} // Pass Lottie loading state to filters
            />
            {!isLoading && hasInitiallyFetched.current && is3DSupported === false && (
                <Box sx={{ width: '100%', maxWidth: '1400px', display: 'flex', justifyContent: 'center', mb: 2, mt: 2 }}>
                    <Paper 
                        elevation={2} 
                        sx={{ 
                            textAlign: 'center', 
                            p: 3, 
                            borderRadius: 4, 
                            background: '#FFF3CD', 
                            width: '100%',
                            backdropFilter: 'blur(16px)'
                        }}
                    >
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                color: '#856404', 
                                fontFamily: "'DM Sans', sans-serif",
                                mb: 1
                            }}
                        >
                            3D MAPPING NOT AVAILABLE IN THIS AREA
                        </Typography>
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: '#856404', 
                                fontFamily: "'DM Sans', sans-serif" 
                            }}
                        >
                            The 3D data we use for sun calculations isn't available here yet. You can still browse pubs but sun exposure information won't be shown.
                        </Typography>
                    </Paper>
                </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2, px: '5%', pb: 4 }}>
                {isCustomSearchMode && (
                    <Box sx={{ width: '100%', maxWidth: '1400px', display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Button variant="outlined" size="small" startIcon={<EditLocationIcon />} onClick={handleEditSearchArea} sx={{ color: themeColors.darkBackground, borderColor: themeColors.orange, textTransform: 'none', borderRadius: 2, px: 3, py: 1, '&:hover': { borderColor: themeColors.orange, backgroundColor: 'rgba(252, 82, 8, 0.04)' } }}>
                            Change search area
                        </Button>
                    </Box>
                )}
                {geoError && !isCustomSearchMode && (
                    <Snackbar open={true} message={geoError} action={ <Button color="inherit" size="small" onClick={() => requestUserLocation()} startIcon={<MyLocationIcon />} sx={{ ':hover': { border: '1px solid white' }, border: '1px solid transparent' }}> Grant Permission </Button> }/>
                )}
                {/* Lottie Loader: Shows only during initial pub list fetch OR geoLoading */}
                {(isLoading && !hasInitiallyFetched.current) && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '50vh', width: '100%' }}><Loader /></Box>
                )}

                {/* Content Area: Shows after initial fetch (Lottie is off) */}
                {/* "No Pubs Found" Message */}
                {!isLoading && hasInitiallyFetched.current && processedPubs.length === 0 && !search && (
                   <Paper elevation={2} sx={{ textAlign: 'center', p: 3, borderRadius: 4, background: themeColors.red, backdropFilter: 'blur(16px)', width: '100%', mt: 2 }}>
                       <Typography variant="h5" sx={{ color: themeColors.darkBackground, fontFamily: "'DM Sans', sans-serif" }}>NO PUBS FOUND MATCHING YOUR FILTERS</Typography>
                       <Typography variant="body1" sx={{ mt: 2, color: 'rgba(34, 34, 34, 0.7)', fontFamily: "'DM Sans', sans-serif" }}>Try adjusting your filters or search in a different location</Typography>
                   </Paper>
                )}
                {/* Pubs List or Map View */}
                {!isLoading && hasInitiallyFetched.current && processedPubs.length > 0 && (
                    tab === 'list' ? (
                        <Box className="pubsList" sx={{ alignItems: 'center', minHeight: '50vh', width: '100%' }}>
                           {processedPubs.map((pub, index) => (
                               <React.Fragment key={pub.google_place_id}>
                                   <PubCard 
                                        pub={pub} 
                                        timelineMode={timelineMode} 
                                        raycastLoadingForThisPub={raycastingPubIds.has(pub.google_place_id)} 
                                        onViewPub={handleViewPub} 
                                        onSubmitGarden={handleSubmitGarden}
                                        is3DSupported={is3DSupported} // Add this new prop
                                    />
                                   {index === 1 && (<AdSense client="ca-pub-3335995580070945" slot="6286987966" format="auto" responsive="true" width="100%" height="200px" />)}
                               </React.Fragment>
                           ))}
                       </Box>
                    ) : (
                        <MapView latitude={latitude} longitude={longitude} pubsToView={processedPubs} />
                    )
                )}
            </Box>
            {tab === 'list' && (<ScrollIndicator iconColor={themeColors.orange} threshold={100} size={32} bottom={10} right={10} hideOnlyAtBottom={true} forceVisible={true} zIndex={2000} />)}
            <Box sx={{ px: '5%' }}><Footer /></Box>
        </Box>
    );
};
export default FindPubs;